import { memo, FocusEvent, useRef, useEffect, ChangeEvent, useState } from 'react';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocale } from '../../../../context/Localization/localization';
import { useField, useFormikContext } from 'formik';
import { PhoneOrEmailFieldMask } from '../PhoneOrEmailFieldMask/PhoneOrEmailFieldMask';
import { PhoneOrEmailFieldMaskInputProps, WebFormPhoneOrEmailFieldProps } from '../FormPhoneOrEmailField.types';
import { PhoneOrEmailFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

/**
 * This component is never used in the component because all implementations of
 * the phone or email field is done exclusively for kiosk.
 *
 * # This has not been tested in any way and may not work.
 */
export const WebFormPhoneOrEmailField = memo(({
    name,
    id,
    title,
    label,
    ariaLabel,
    disabled,
    required,
    autoFocus,
    capsLock,
    fullWidth,
    seventyFivePercentWidth,
    tabFocusRefContext,
    initiallyMasked,
}: WebFormPhoneOrEmailFieldProps) => {
    const [field, meta] = useField<string>({
        name: name,
        validate: PhoneOrEmailFieldValidation({
            required: required ?? false,
            label,
            name,
        }),
    });
    const { submitCount, setFieldValue } = useFormikContext();

    /**
     * A ref to the HTMLInputElement in the MUI TextField.
     */
    const inputRef = useRef<HTMLInputElement>(null);
    /**
     * The focus ref to prevent a stale state when the on-screen keyboard
     * calls any functions that need to know if the field is focused or not.
     */
    const isFocused = useRef<boolean>(false);
    /**
     * Boolean state to update the email pattern mask once the field has been focused
     * so that it never remasks the value.
     */
    const initialUnmask = useRef<boolean>(false);
    /**
     * Boolean state to update the MUI TextField label's position since it does not always
     * float up when the field has a value.
     */
    const [shrink, setShrink] = useState<boolean>(false);
    /**
     * State value to force an update to the pattern mask component and pass the formik
     * value as well as whether the value should be masked.
     */
    const [maskProps, setMaskProps] = useState<PhoneOrEmailFieldMaskInputProps>({
        value: field.value ?? '',
        isMasked: false,
    });
    useScrollToOnError({
        scrollable: inputRef,
        name: name,
    });

    const {
        dealer: { primaryLanguage },
    } = useKioskState();
    const locale = useLocale();

    useEffect(() => {
        if (tabFocusRefContext) {
            tabFocusRefContext.current = inputRef.current;
        }
    }, [tabFocusRefContext]);

    useEffect(() => {
        /**
         * When the formik value is not the same as the internal value, we update it to the formik value.
         * We then check to see if the value is empty or null and either update the hook directly via the maskRef
         * or if it is not null or empty update the hook via the setValue function. We also determine whether to
         * mask an email when doing so.
         */
        if (field.value.length > 0 && shrink !== true) {
            /**
             * RegExp to validate that the email is in a state where we would mask it.
             */
            const maskEmailRegex: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/gu;
            /**
             * RegExp to validate that the string is a phone number for masking.
             */
            const maskPhoneRegex: RegExp = /^\d{1,10}$/gu;

            /**
             * Handles correctly masking the email if we need to mask the email before displaying it.
             */
            const maskEmail = (value: string): string => {
                const emailBeforeAt: string = value.substring(0, value.indexOf('@'));
                const emailAfterAt: string = value.substring(value.indexOf('@'));

                return `${emailBeforeAt.slice(0, 2)}******${emailBeforeAt.slice(-2)}${emailAfterAt}`;
            };

            setShrink(true);

            if (!isFocused.current && initiallyMasked && !initialUnmask.current) {
                if (maskEmailRegex.test(field.value)) {
                    setMaskProps({
                        value: maskEmail(field.value),
                        isMasked: true,
                    });
                }
                else if (maskPhoneRegex.test(field.value)) {
                    setMaskProps((prevMaskProps: PhoneOrEmailFieldMaskInputProps) => ({
                        ...prevMaskProps,
                        isMasked: true,
                    }));
                }
            }
        }
    }, [field.value, initiallyMasked, maskProps, shrink]);

    const handleOnBlur = () => {
        isFocused.current = false;
        if (maskProps.value.length === 0) {
            setShrink(false);
        }
    };

    const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value
            .replace(/[\u2018\u2019\u201A]/g, '\'')
            .replace(/[\u201C\u201D\u201E]/g, '"')
            .replace(/\u2026/g, '...')
            .replace(/[\u2013\u2014]/g, '-');
        setMaskProps((prevMaskProps: PhoneOrEmailFieldMaskInputProps) => ({
            ...prevMaskProps,
            value: value,
        }));
        setFieldValue(name, value);
    };

    const handleOnFocus = (_event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (maskProps.isMasked) {
            setMaskProps({
                value: '',
                isMasked: false,
            });
            setFieldValue(name, '');
        }
        if (!initialUnmask.current) {
            initialUnmask.current = true;
        }
        isFocused.current = true;
        setShrink(true);
    };

    return (
        <BaseTextField
            name={name}
            id={id}
            title={title}
            label={label}
            ariaLabel={ariaLabel}
            inputMode={'text'}
            type={'text'}
            inputRef={inputRef}
            error={meta.error}
            touched={meta.touched || submitCount > 0}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            fullWidth={fullWidth}
            seventyFivePercentWidth={seventyFivePercentWidth}
            capsLock={capsLock}
            onFocus={handleOnFocus}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
            InputLabelProps={{
                shrink: shrink,
            }}
            InputProps={{
                inputComponent: PhoneOrEmailFieldMask as any,
                inputProps: {
                    ...maskProps,
                },
            }}
        />
    );
},
);